/**
 * React Query query hooks for benefit classes data fetching
 *
 * These hooks provide caching, automatic refetching, and loading states
 * for read operations.
 */

import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { queryKeys as planGridQueryKeys } from "@platform/plan-grid-core";

import { listBenefitClasses } from "./client";
import { BenefitClassResponse } from "./client";

/**
 * Query keys for React Query cache management
 */
export const queryKeys = {
  benefitClasses: planGridQueryKeys.benefitClasses,
};

/**
 * Hook to fetch benefit classes for an RFP
 *
 * @param rfpId - RFP ID to fetch benefit classes for
 * @param options - React Query options
 * @returns Query result with benefit classes data
 *
 * @example
 * ```tsx
 * function BenefitClassList({ rfpId }: { rfpId: string }) {
 *   const { data: classes, isLoading, error } = useBenefitClasses(rfpId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return (
 *     <ul>
 *       {classes?.map(bc => <li key={bc.id}>{bc.name}</li>)}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useBenefitClasses = (
  rfpId: string,
  options?: Omit<
    UseQueryOptions<BenefitClassResponse[], Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<BenefitClassResponse[], Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: queryKeys.benefitClasses(rfpId),
    queryFn: () => listBenefitClasses(rfpId, true, axiosInstance),
    enabled: !!rfpId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};
